import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Spinner,
} from "@chakra-ui/react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { z } from "zod";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const setupSchema = z.object({
  password: z.string().min(1, "Password is required"),
});

type SetupFormData = z.infer<typeof setupSchema>;

export function TwoFactorSetupForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [userEmail, setUserEmail] = useState("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enable2FASetup } = useAuthStore();
  const { settings: siteSettings } = useSiteSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetupFormData>({
    resolver: zodResolver(setupSchema),
  });

  useEffect(() => {
    const email = searchParams.get("email");
    if (email) {
      setUserEmail(email);
    } else {
      const timer = setTimeout(() => {
        const emailCheck = new URLSearchParams(window.location.search).get(
          "email"
        );
        if (!emailCheck) {
          navigate("/login");
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [searchParams, navigate]);

  const onSubmit = async (data: SetupFormData) => {
    if (!userEmail) {
      setError("Email is required");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const result = await enable2FASetup(userEmail, data.password);
      const rememberMe = searchParams.get("remember_me") === "1";

      if (result.success && result.twoFactorRequired) {
        navigate(
          `/two-factor?email=${encodeURIComponent(userEmail)}&remember_me=${
            rememberMe ? "1" : "0"
          }`
        );
      } else {
        setError(result.message || "Failed to enable 2FA. Please try again.");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || "Failed to enable 2FA"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (!userEmail) {
    return null;
  }

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgImage="url('/assets/img/bg.jpg')"
      bgPosition="center"
      bgRepeat="no-repeat"
      bgSize="cover"
      px={{ base: 4, sm: 6 }}
    >
      <Card
        w="full"
        maxW="448px"
        borderRadius="20px"
        boxShadow="0 10px 30px rgba(0,0,0,0.06)"
        bg="white"
      >
        <CardBody p={{ base: 6, sm: 8, md: 10 }}>
          <VStack spacing={{ base: 4, md: 6 }}>
            {/* Logo */}
            <Box textAlign="center" mb={{ base: 1, md: 2 }}>
              <img
                src={siteSettings.auth_logo || "/assets/img/logo.jpg"}
                alt="Logo"
                style={{
                  maxWidth: "120px",
                  width: "100%",
                  margin: "0 auto",
                  opacity: 0.9,
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Heading */}
            <VStack spacing={2}>
              <Heading
                size={{ base: "sm", md: "md" }}
                fontWeight="700"
                color="gray.800"
                textAlign="center"
                px={{ base: 2, sm: 0 }}
              >
                Two-Factor Authentication Required
              </Heading>
              <Text
                fontSize={{ base: "xs", sm: "sm" }}
                color="gray.600"
                textAlign="center"
                px={{ base: 2, sm: 0 }}
              >
                Your administrator has made two-factor authentication mandatory
                for all users.
              </Text>
              <Text
                fontSize={{ base: "xs", sm: "sm" }}
                color="gray.600"
                textAlign="center"
                px={{ base: 2, sm: 0 }}
              >
                Please enable 2FA to continue logging in.
              </Text>
              <Text
                fontSize={{ base: "xs", sm: "sm" }}
                color="orange.600"
                fontWeight="500"
                mt={1}
                wordBreak="break-word"
                px={{ base: 2, sm: 0 }}
              >
                {userEmail}
              </Text>
            </VStack>

            {error && (
              <Alert
                status="error"
                borderRadius="md"
                fontSize={{ base: "xs", sm: "sm" }}
              >
                <AlertIcon />
                <Text fontSize={{ base: "xs", sm: "sm" }}>{error}</Text>
              </Alert>
            )}

            <Alert
              status="info"
              borderRadius="md"
              fontSize={{ base: "xs", sm: "sm" }}
            >
              <AlertIcon />
              <Text fontSize={{ base: "xs", sm: "sm" }}>
                You'll need to enter your password again to enable 2FA. After
                enabling, you'll receive a verification code via email.
              </Text>
            </Alert>

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="full">
              <VStack spacing={{ base: 4, md: 5 }} w="full">
                <FormControl isInvalid={!!errors.password}>
                  <FormLabel
                    fontSize={{ base: "xs", sm: "sm" }}
                    fontWeight="500"
                    color="gray.700"
                  >
                    Password
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      color="gray.400"
                      h={{ base: "40px", md: "48px" }}
                    >
                      <Lock size={18} />
                    </InputLeftElement>
                    <Input
                      {...register("password")}
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      borderColor="gray.300"
                      borderRadius="8px"
                      _hover={{ borderColor: "gray.400" }}
                      _focus={{
                        borderColor: "orange.500",
                        boxShadow: "0 0 0 1px #ed8936",
                      }}
                      fontSize={{ base: "xs", sm: "sm" }}
                      h={{ base: "40px", md: "48px" }}
                    />
                    <InputRightElement
                      width="3rem"
                      h={{ base: "40px", md: "48px" }}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        p={0}
                        type="button"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        h="1.75rem"
                        minW="1.75rem"
                        _hover={{ bg: "transparent" }}
                      >
                        {showPassword ? (
                          <EyeOff size={18} color="#718096" />
                        ) : (
                          <Eye size={18} color="#718096" />
                        )}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage fontSize="xs">
                    {errors.password?.message}
                  </FormErrorMessage>
                </FormControl>

                <Button
                  type="submit"
                  w="full"
                  borderRadius="10px"
                  background="linear-gradient(90deg, #dc3611 0%, #fa886f 100%)"
                  color="white"
                  size={{ base: "md", md: "lg" }}
                  fontSize={{ base: "sm", md: "md" }}
                  fontWeight="600"
                  _hover={{
                    background:
                      "linear-gradient(90deg, #c92f0e 0%, #f97960 100%)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(220, 54, 17, 0.3)",
                  }}
                  _active={{
                    background:
                      "linear-gradient(90deg, #b32a0d 0%, #f86951 100%)",
                    transform: "translateY(0)",
                  }}
                  isDisabled={isLoading}
                  transition="all 0.2s"
                >
                  {isLoading ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Spinner size="sm" color="white" thickness="2px" />
                      <Text>Enabling 2FA...</Text>
                    </Box>
                  ) : (
                    "Enable Two-Factor Authentication"
                  )}
                </Button>
              </VStack>
            </Box>

            <Button
              variant="link"
              color="gray.600"
              fontSize={{ base: "xs", sm: "sm" }}
              fontWeight="500"
              onClick={handleBackToLogin}
              _hover={{ color: "orange.500" }}
            >
              Back to Login
            </Button>
          </VStack>
        </CardBody>
      </Card>
      <Text
        fontSize={{ base: "14px", sm: "14px" }}
        color="gray.900"
        position="fixed"
        bottom={{ base: "20px", md: "20px" }}
        fontWeight="400"
        textAlign="center"
        px={4}
      >
        Copyright ©2025 – ActionLabs
      </Text>
    </Box>
  );
}
